/**
 * PAI Chat Widget
 *
 * Floating AI assistant chat widget with:
 * - Floating button (bottom-right)
 * - Expandable chat window
 * - Message history with typing indicators
 * - Feedback buttons (thumbs up/down)
 * - Context-aware responses (ViewerProfile + RolePersona)
 */

import { useState, useEffect, useRef } from 'react'
import './PAIChatWidget.css'
import { useViewerProfile } from '@/contexts/ViewerProfileContext'
import { useRolePersona } from '@/contexts/RolePersonaContext'
import { usePAIChat, usePAIFeedback, getOrCreateSessionId, type ChatMessage } from '@/hooks/api/usePAI'

export interface PAIChatWidgetProps {
  /** AI Persona ID to chat with */
  personaId: string
  /** Position of floating button (default: 'bottom-right') */
  position?: 'bottom-right' | 'bottom-left'
  /** Initial greeting message (optional) */
  greeting?: string
}

export function PAIChatWidget({
  personaId,
  position = 'bottom-right',
  greeting = "Hi! I'm Agustin's AI assistant. Ask me anything about his work, experience, or projects.",
}: PAIChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [sessionId] = useState(() => getOrCreateSessionId())

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Context hooks
  const { currentProfile } = useViewerProfile()
  const { currentPersona } = useRolePersona()

  // API hooks
  const chatMutation = usePAIChat()
  const feedbackMutation = usePAIFeedback()

  // Add greeting message on mount
  useEffect(() => {
    if (greeting) {
      setMessages([
        {
          role: 'assistant',
          content: greeting,
          timestamp: Date.now(),
          id: 'greeting',
        },
      ])
    }
  }, [greeting])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Handle send message
  const handleSend = async () => {
    if (!inputValue.trim() || chatMutation.isPending) return

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue,
      timestamp: Date.now(),
      id: `user-${Date.now()}`,
    }

    // Add user message to UI
    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    try {
      // Send to API with context
      const response = await chatMutation.mutateAsync({
        personaId,
        sessionId,
        message: inputValue,
        context: {
          viewerProfile: currentProfile,
          rolePersona: currentPersona,
        },
      })

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: Date.now(),
        id: `assistant-${Date.now()}`,
      }

      setMessages((prev) => [...prev, assistantMessage])
    } catch (error) {
      console.error('Chat error:', error)

      // Add error message
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: Date.now(),
        id: `error-${Date.now()}`,
      }

      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsTyping(false)
    }
  }

  // Handle feedback
  const handleFeedback = async (messageId: string, isPositive: boolean) => {
    try {
      await feedbackMutation.mutateAsync({
        personaId,
        interactionId: messageId,
        sessionId,
        signalType: isPositive ? 'thumbs_up' : 'thumbs_down',
        signalValue: { helpful: isPositive },
        userContext: {
          viewerProfile: currentProfile,
          rolePersona: currentPersona,
        },
      })

      // Update message to show feedback was submitted
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, feedbackSubmitted: isPositive } : msg
        )
      )
    } catch (error) {
      console.error('Feedback error:', error)
    }
  }

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Build position classes
  const buttonPositionClass = position === 'bottom-right' ? 'pai-chat-btn--br' : 'pai-chat-btn--bl'

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          className={`pai-chat-btn ${buttonPositionClass}`}
          onClick={() => setIsOpen(true)}
          aria-label="Open AI assistant chat"
        >
          <svg
            className="pai-chat-btn__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="pai-chat-btn__badge">AI</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`pai-chat-window ${buttonPositionClass}`}>
          {/* Header */}
          <div className="pai-chat-window__header">
            <div className="pai-chat-window__header-content">
              <div className="pai-chat-window__avatar">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 1 1-8 0V6a4 4 0 0 1 4-4z" />
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                </svg>
              </div>
              <div className="pai-chat-window__title-group">
                <h3 className="pai-chat-window__title">AI Assistant</h3>
                <p className="pai-chat-window__subtitle">Powered by PAI</p>
              </div>
            </div>
            <button
              className="pai-chat-window__close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="pai-chat-window__messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`pai-message pai-message--${message.role}`}
              >
                <div className="pai-message__content">
                  {message.content}
                </div>

                {/* Feedback buttons (only for assistant messages) */}
                {message.role === 'assistant' && message.id !== 'greeting' && (
                  <div className="pai-message__feedback">
                    <button
                      className={`pai-message__feedback-btn ${
                        (message as any).feedbackSubmitted === true
                          ? 'pai-message__feedback-btn--active'
                          : ''
                      }`}
                      onClick={() => handleFeedback(message.id!, true)}
                      aria-label="This was helpful"
                      disabled={feedbackMutation.isPending}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                      </svg>
                    </button>
                    <button
                      className={`pai-message__feedback-btn ${
                        (message as any).feedbackSubmitted === false
                          ? 'pai-message__feedback-btn--active'
                          : ''
                      }`}
                      onClick={() => handleFeedback(message.id!, false)}
                      aria-label="This was not helpful"
                      disabled={feedbackMutation.isPending}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ transform: 'rotate(180deg)' }}
                      >
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="pai-message pai-message--assistant">
                <div className="pai-message__content">
                  <div className="pai-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="pai-chat-window__input-area">
            <textarea
              ref={inputRef}
              className="pai-chat-window__input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything..."
              rows={1}
              disabled={chatMutation.isPending}
              aria-label="Chat message input"
            />
            <button
              className="pai-chat-window__send-btn"
              onClick={handleSend}
              disabled={!inputValue.trim() || chatMutation.isPending}
              aria-label="Send message"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>

          {/* Context indicator (for debugging) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="pai-chat-window__debug">
              Context: {currentProfile} / {currentPersona}
            </div>
          )}
        </div>
      )}
    </>
  )
}
